import signatureHref from "../styles/signature.css?url";
import studioHref from "../styles/studio.css?url";
import { palettes, normaliseTheme, darkThemes } from "./themeCatalog.js";
export {
  palettes,
  themes,
  themeFamilies,
  themeGroups,
  normaliseTheme,
} from "./themeCatalog.js";

export const themeStorageKey = (user) =>
  `claimsdesk:theme:${user?.id ?? "anon"}`;
export function readTheme(user) {
  try {
    return normaliseTheme(
      localStorage.getItem(themeStorageKey(user)) || user?.theme,
    );
  } catch {
    return normaliseTheme(user?.theme);
  }
}

function ensureLink(id, href) {
  let link = document.getElementById(id);
  if (!link) {
    link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.getAttribute("href") !== href) link.href = href;
  return link;
}

/** Appearance only: the shared Claimsdesk layout owns all column/rail widths. */
export function applyTheme(value) {
  const theme = normaliseTheme(value),
    palette = palettes[theme];
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.bsTheme = darkThemes.includes(theme) ? "dark" : "light";
  const base = ensureLink(
    "theme-stylesheet",
    __THEME_ASSETS__[palette ? "claimsdesk" : theme],
  );
  const sheets = [base];
  if (palette) {
    root.dataset.uiPalette = palette.palette;
    root.dataset.uiFamily = palette.family;
    const link = ensureLink(
      "claimsdesk-overrides",
      palette.family === "studio" ? studioHref : signatureHref,
    );
    link.media = "all";
    document.head.appendChild(link);
    sheets.push(link);
  } else {
    delete root.dataset.uiPalette;
    delete root.dataset.uiFamily;
    const link = document.getElementById("claimsdesk-overrides");
    if (link) link.media = "not all";
  }
  const ready = () => {
    if (
      root.dataset.theme !== theme ||
      !document.body ||
      sheets.some((sheet) => sheet.sheet?.href !== sheet.href)
    )
      return;
    // Pale primaries need dark selected-option/calendar text. Measure the
    // loaded theme, not the previous stylesheet during an asynchronous swap.
    const probe = document.createElement("button");
    probe.className = "btn btn-primary";
    probe.style.display = "none";
    document.body.appendChild(probe);
    const rgb = getComputedStyle(probe)
      .backgroundColor.match(/[\d.]+/g)
      ?.slice(0, 3)
      .map(Number);
    probe.remove();
    if (rgb?.length === 3) {
      const linear = rgb.map((c) => {
        c /= 255;
        return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
      });
      const luminance =
        linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
      root.style.setProperty(
        "--cd-selection-ink",
        1.05 / (luminance + 0.05) >= 4.5 ? "#fff" : "#000",
      );
    }
    window.dispatchEvent(new Event("claimsdesk:theme-changed"));
  };
  sheets.forEach((sheet) => {
    sheet.onload = ready;
  });
  if (sheets.every((sheet) => sheet.sheet?.href === sheet.href))
    requestAnimationFrame(ready);
}
